import { useJobillaOrgId } from '@/hooks'
import { apiErrorMessage } from '@/lib'
import {
	campaignsShowQueryKey,
	ThankYouScreenData,
	useCampaignsShow,
	useThankYouScreenBulkUpdate,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import i18next from 'i18next'
import { useAtomValue } from 'jotai'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef } from 'react'
// eslint-disable-next-line import/no-internal-modules
import { thankYouScreenBulkReset, thankYouScreenReset } from '../../../../../packages/nexus-sdk/lib/axios'
import {
	DEFAULT_THANK_YOU_SCREEN_DESCRIPTION,
	DEFAULT_THANK_YOU_SCREEN_TITLE,
	emitUnhandledError,
	logger,
	ScreenChangeInfo,
	ThankYouScreen,
	thankYouScreenDebounceValueAtom,
	useThankYouScreenLibrary,
} from '../../ui'
import { replaceCachedArray, updateCachedArrayItem } from '../helpers'

export function useNewThankYouScreenEditor(campaignId: string) {
	const queryClient = useQueryClient()
	const jobillaOrgId = useJobillaOrgId()
	const campaign = useCampaignsShow(campaignId)

	const formData = campaign.data?.form

	const thankYouScreensDebounceValue = useAtomValue(thankYouScreenDebounceValueAtom)

	// Get all thank you screens sorted by min_score_range descending (highest first)
	const thankYouScreensData: ThankYouScreenData[] = useMemo(() => {
		const screens = formData?.thankYouScreens ?? []
		return [...screens].sort((a, b) => (b.minScoreRange ?? 0) - (a.minScoreRange ?? 0))
	}, [formData?.thankYouScreens])

	const thankYouScreens: ThankYouScreen[] = useMemo(() => {
		return thankYouScreensData.map((data) => ({
			id: data.id ?? '',
			title: data.title ?? DEFAULT_THANK_YOU_SCREEN_TITLE,
			description: data.description ?? DEFAULT_THANK_YOU_SCREEN_DESCRIPTION,
			min_score_range: data.minScoreRange ?? 0,
			max_score_range: data.maxScoreRange ?? 100,
			organisation_name: data.organisationName ?? null,
			logo: data.logo ?? null,
		}))
	}, [thankYouScreensData])

	const bulkUpdateMutation = useThankYouScreenBulkUpdate({
		mutationKey: ['bulk-update-thank-you-screens', campaignId],
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
		onError: (error) => {
			logger.error(error, {
				tags: { component: 'ThankYouScreenEditor', action: 'bulk-update' },
			})
			emitUnhandledError({
				title: i18next.t('editor:errors.failedToSaveThankYouPages'),
				description: apiErrorMessage(error) ?? i18next.t('editor:errors.unexpectedError'),
				cause: error,
			})
		},
	})

	const saveAllScreens = useCallback(async (screens: ThankYouScreen[]) => {
		if (screens.length === 0) {
			return
		}

		await bulkUpdateMutation.mutateAsync({
			campaign: campaignId,
			data: {
				screens: screens.map((screen) => ({
					id: screen.id,
					title: screen.title,
					description: screen.description,
				})),
			},
		})
	}, [bulkUpdateMutation, campaignId])

	const handleScreenChangeRef = useRef<(info: ScreenChangeInfo) => void>(() => {})

	useEffect(() => {
		handleScreenChangeRef.current = (info: ScreenChangeInfo) => {
			const originalData = thankYouScreensData.find((d) => d.id === info?.screen?.id)
			if (!originalData) {
				return
			}

			logger.track('thank_you_screen_updated', {
				data: {
					id: originalData.id,
					organisation_id: jobillaOrgId,
					campaign_id: campaignId,
					campaign_language: campaign.data?.language ?? 'unknown',
					score_bucket: `${originalData.minScoreRange}-${originalData.maxScoreRange}`,
					field_changed: info.field,
				},
			})
		}
	}, [
		campaign.data?.language,
		campaignId,
		jobillaOrgId,
		thankYouScreensData,
	])

	const handleScreenChange = useMemo(
		() =>
			debounce((info: ScreenChangeInfo) => {
				handleScreenChangeRef.current(info)
			}, thankYouScreensDebounceValue),
		[thankYouScreensDebounceValue],
	)

	useEffect(() => {
		return () => {
			handleScreenChange.flush()
		}
	}, [handleScreenChange])

	const isGenerating = thankYouScreensData.some(
		(screen) => screen.startedGenerationAt != null && screen.finishedGenerationAt == null,
	)

	const latestVersion = useMemo(() => {
		const dates = thankYouScreensData
			.map((s) => s.updatedAt)
			.filter((d): d is string => d != null)
		if (dates.length === 0) {
			return null
		}
		return dates.sort().pop() ?? null
	}, [thankYouScreensData])

	const handleResetScreen = useCallback(
		async (screen: ThankYouScreen) => {
			handleScreenChange.flush()

			try {
				const updatedScreen = await thankYouScreenReset(screen.id)

				queryClient.setQueryData(
					campaignsShowQueryKey(campaignId),
					(old) => updateCachedArrayItem(old, 'form.thank_you_screens', updatedScreen.data),
				)
			} catch (error) {
				logger.error(error, {
					tags: { component: 'ThankYouScreenEditor', action: 'reset-screen' },
				})
				emitUnhandledError({
					title: i18next.t('editor:errors.failedToResetThankYouPage'),
					description: i18next.t('editor:errors.unexpectedError'),
					cause: error,
				})
			} finally {
				logger.track('thank_you_screen_reset', {
					data: {
						id: screen.id,
						organisation_id: jobillaOrgId,
						campaign_id: campaignId,
						campaign_language: campaign.data?.language ?? 'unknown',
						score_bucket: `${screen.min_score_range}-${screen.max_score_range}`,
					},
				})
			}
		},
		[
			queryClient,
			handleScreenChange,
			jobillaOrgId,
			campaignId,
			campaign.data?.language,
		],
	)

	const handleResetAllScreens = useCallback(
		async () => {
			handleScreenChange.flush()

			try {
				const defaultScreens = await thankYouScreenBulkReset(campaignId)

				queryClient.setQueryData(
					campaignsShowQueryKey(campaignId),
					(old) => replaceCachedArray(old, 'form.thank_you_screens', defaultScreens.data),
				)
			} catch (error) {
				logger.error(error, {
					tags: { component: 'ThankYouScreenEditor', action: 'reset-all-screens' },
				})
				emitUnhandledError({
					title: i18next.t('editor:errors.failedToResetThankYouPages'),
					description: i18next.t('editor:errors.unexpectedError'),
					cause: error,
				})
			} finally {
				logger.track('thank_you_screen_reset_all', {
					data: {
						organisation_id: jobillaOrgId,
						campaign_id: campaignId,
						campaign_language: campaign.data?.language ?? 'unknown',
					},
				})
			}
		},
		[
			queryClient,
			handleScreenChange,
			jobillaOrgId,
			campaignId,
			campaign.data?.language,
		],
	)

	const { store } = useThankYouScreenLibrary({
		screens: thankYouScreens,
		isPending: campaign.isPending,
		isGenerating,
		onSave: saveAllScreens,
		onScreenChange: handleScreenChange,
		onResetScreen: handleResetScreen,
		onResetAllScreens: handleResetAllScreens,
		version: latestVersion,
	})

	return { store }
}
